import React from 'react';
import { escape as escapeHtml } from 'lodash';
import { Typography } from 'antd';

import { dates } from '~library/dates';

import { CustomTooltip, TOOLTIP_ROOT_ID } from '../../../components/Tooltip';
import type { CacheState } from './CachedPage';

// Shown instead of a label for states rendered as a placeholder, and for a cache state outside
// `CacheState` altogether (a legacy value the backend can still send).
const UNRECOGNIZED_CACHE_STATE_LABEL = '---';
const RECACHE_HINT = 'Recache this URL to retry it.';
const GENERIC_FAILURE_EXPLANATION = 'Recaching failed repeatedly.';

type CacheStateEntry = {
  label: string;
  tooltip?: string;
};

// The State column's filter options and the state renderer share this record as their single
// source. It is exhaustive over `CacheState`, so a new state cannot be added to the union without
// deciding here how the table renders it. A `null` entry means "renders the placeholder, and is
// not offered as a filter option".
const cacheStates: Readonly<Record<CacheState, CacheStateEntry | null>> = {
  Cached: { label: 'Cached', tooltip: 'The URL has been cached' },
  'Caching...': { label: 'Caching...', tooltip: 'The URL is currently being cached' },
  'Recaching...': { label: 'Recaching...', tooltip: 'The URL is currently being recached' },
  // Deliberately unrendered and unfilterable: predates PRE-2429 and is out of PRE-3322's scope.
  'Not Cached': null,
  Failed: { label: 'Failed' },
};

type CacheStateFilterOption = {
  text: string;
  value: CacheState;
};

export const CACHE_STATE_FILTER_OPTIONS: readonly CacheStateFilterOption[] = Object.entries(
  cacheStates
).flatMap(([name, entry]) => (entry ? [{ text: entry.label, value: name as CacheState }] : []));

// A Failed row's Recache button is emphasized, making recovery the obvious action; every other
// state (including the unrecognized fall-through) keeps antd's default button style.
export function recacheButtonType(cacheStateName: CacheState): 'primary' | undefined {
  return cacheStateName === 'Failed' ? 'primary' : undefined;
}

type FailureDetail = {
  recacheAttempts?: number;
  lastRecacheError?: string | null;
  lastRecacheErrorAt?: string | null;
};

function buildFailedTooltipHtml({ recacheAttempts, lastRecacheError, lastRecacheErrorAt }: FailureDetail): string {
  // `recacheAttempts` is typed `number` but comes from a raw `select *` payload, so coerce it
  // before interpolating: a non-numeric runtime value must never reach `data-tooltip-html`.
  const attempts = Number(recacheAttempts);
  const hasAttempts = Number.isFinite(attempts) && attempts > 0;
  const hasFailureDetail = Boolean(lastRecacheError) || Boolean(lastRecacheErrorAt) || hasAttempts;

  const lines = [
    `<b>Recaching failed</b>`,
    hasFailureDetail ? undefined : GENERIC_FAILURE_EXPLANATION,
    // `last_recache_error` is arbitrary backend text and lands in `data-tooltip-html`, which
    // GlobalTooltipRoot renders as (DOMPurify-sanitized) HTML — escape it so it stays inert text.
    lastRecacheError ? `Last error: ${escapeHtml(lastRecacheError)}` : undefined,
    lastRecacheErrorAt ? `Failed at: ${dates.pointInTimeWithAge(lastRecacheErrorAt)}` : undefined,
    hasAttempts ? `Failed attempts: ${attempts}` : undefined,
    RECACHE_HINT,
  ].filter((line): line is string => Boolean(line));

  return `<span>${lines.join('<br />')}</span>`;
}

type CacheStateCellProps = FailureDetail & { cacheStateName: CacheState };

export function CacheStateCell({
  cacheStateName,
  recacheAttempts,
  lastRecacheError,
  lastRecacheErrorAt,
}: CacheStateCellProps) {
  // `cacheStateName` is typed `CacheState` but comes from a raw `select *` payload, so the lookup
  // can still miss at runtime; the own-property guard keeps inherited keys (`'constructor'`) and
  // unknown states on the placeholder branch below.
  const cacheState: CacheStateEntry | null = Object.prototype.hasOwnProperty.call(
    cacheStates,
    cacheStateName
  )
    ? cacheStates[cacheStateName]
    : null;

  if (!cacheState) return <>{UNRECOGNIZED_CACHE_STATE_LABEL}</>;

  if (cacheStateName === 'Failed') {
    return (
      <CustomTooltip
        placement="top"
        message={buildFailedTooltipHtml({ recacheAttempts, lastRecacheError, lastRecacheErrorAt })}
      >
        <Typography.Text type="danger">{cacheState.label}</Typography.Text>
      </CustomTooltip>
    );
  }

  return (
    <div
      data-tooltip-id={TOOLTIP_ROOT_ID}
      data-tooltip-content={cacheState.tooltip}
      data-tooltip-place="top"
    >
      {cacheState.label}
    </div>
  );
}
